<script lang="ts">
  import { onMount } from "svelte";
  import { portal } from "svelte-portal/src/Portal.svelte";
  import { onDestroy } from "svelte";
  import { addToBagBar } from "$lib/stores/addToBagBar";
  import { expandedGallery } from "$lib/stores/expandedGallery";
  import { page } from "$app/stores";

  let isBrowser = typeof window !== "undefined";

  /** @type {string} key - Zendesk client ID */
  export let key = "";
  $: pdp =
    $page &&
    $page?.params &&
    Object.prototype.hasOwnProperty.call($page?.params, "slug");

  const LOCALSTORAGE_KEY = "zendesk_opened";
  let zendeskLoadObserverTimer: any;
  let zendeskLoaded = false;
  let loading = false;
  let viewportWidth: number;
  let containerMax = 2560;
  let rightOffset: any;

  onMount(() => {
    const bodyStyle = getComputedStyle(document.body);
    containerMax = Number(
      bodyStyle.getPropertyValue("--container-max").replace(/\D/g, ""),
    );
  });

  $: if (viewportWidth >= containerMax) {
    rightOffset = (viewportWidth - containerMax) / 2;
  }

  let zE: any;

  // Update default widget settings for PDPs
  const updateWidgetSettings = () => {
    zE &&
      zE("webWidget", "updateSettings", {
        webWidget: {
          launcher: {
            labelVisible: false,
          },
          offset: {
            vertical: pdp ? "88px" : "16px",
            horizontal: rightOffset ? `${rightOffset}px` : "0px",
            mobile: {
              vertical: pdp ? "72px" : "16px",
            },
          },
        },
      });
  };

  function loadZendesk(callback: any) {
    // Insert Zendesk script tag
    const zdScript = document.createElement("script");
    zdScript.setAttribute("id", "ze-snippet");
    zdScript.src = `https://static.zdassets.com/ekr/snippet.js?key=${key}`;
    document.getElementsByTagName("body")[0].appendChild(zdScript);
    // Wait for `window.zE` to become available, then execute `callback`
    zendeskLoadObserverTimer = setInterval(function () {
      if (
        typeof (window as any).zE !== "undefined" &&
        typeof (window as any).zE.activate !== "undefined"
      ) {
        clearInterval(zendeskLoadObserverTimer);
        callback();
      }
    }, 100);
  }

  onDestroy(() => clearInterval(zendeskLoadObserverTimer));

  function loadAndOpenZendeskChat() {
    // Initiate loading of the Zendesk library, and activate when done. Set
    // a flag in `sessionStorage` so we can load the library immediately on
    // subsequent page loads
    loading = true;

    sessionStorage.setItem(LOCALSTORAGE_KEY, "true");
    loadZendesk(function () {
      window.setTimeout(function () {
        (window as any).zE.activate();
        zendeskLoaded = true;
        updateWidgetSettings();
      }, 1000);
    });
  }

  onMount(() => {
    if (sessionStorage.getItem(LOCALSTORAGE_KEY)) {
      // Our proxy button was previously clicked, so proceed immediately to
      // load the Zendesk library, so the user has UI continuity
      loadZendesk(() => {});
      zendeskLoaded = true;
    }
  });

  $: if ($page?.url.searchParams.get("livechat") === "true" && isBrowser) {
    if (
      typeof (window as any).zE === "undefined" ||
      typeof (window as any).zE.activate === "undefined"
    ) {
      loadAndOpenZendeskChat();
    } else {
      (window as any).zE.activate();
      updateWidgetSettings();
    }
    const url = new URL(window.location.toString());
    url.searchParams.delete("livechat");
    history.replaceState({}, "", url);
  }

  // If Zendesk is intialised, reset widget settings on route change
  $: if ($page && isBrowser) {
    if (typeof (window as any).zE !== "undefined") {
      updateWidgetSettings();
    }
  }
</script>

<style lang="scss">
  :global(#launcher) {
    display: none !important; //hide Zendesk's default launcher CTA
  }

  .wrapper {
    pointer-events: none;
    position: fixed;
    z-index: 10;
    width: 100%;
    max-width: $container-max;
    display: flex;
    justify-content: flex-end;
    padding-right: 16px;
    bottom: 16px;

    @include breakpoint(md) {
      padding-right: 24px;
      bottom: 24px;
      transition: bottom 350ms ease-in-out;
    }

    &--pdp {
      bottom: 88px;

      @include breakpoint(md) {
        bottom: 112px;
        transition: bottom 350ms ease-in-out;
      }
    }
  }

  .zendesk {
    display: inline-flex;
    align-items: center;
    padding: 10px;
    background: $on-surface;
    border: 0;
    border-radius: 100vw;
    font-family:
      system-ui,
      -apple-system,
      BlinkMacSystemFont,
      Segoe UI,
      Roboto,
      Oxygen-Sans,
      Ubuntu,
      Cantarell,
      "Helvetica Neue",
      Arial,
      sans-serif;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.6px;
    line-height: 1;
    color: $surface;

    &:active,
    &:focus {
      outline: none;
      box-shadow: inset 0 0 0 3px rgb(255 255 255 / 40%);
    }

    &__icon {
      min-width: 20px;
      min-height: 20px;
      height: 20px;
      width: 20px;
      fill: currentColor;
    }

    &__label {
      margin-left: 10px;
    }
  }

  #myLauncher {
    pointer-events: all;
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

<div
  class={`wrapper ${
    pdp && ($addToBagBar || $expandedGallery) ? "wrapper--pdp" : ""
  }`}
  use:portal
>
  {#if !zendeskLoaded}
    <button
      id="myLauncher"
      class={`zendesk `}
      aria-label="Launch Zendesk chat"
      on:click={() => {
        loadAndOpenZendeskChat();
      }}
      hidden
    >
      <svg class="zendesk__icon" viewBox="0 0 15 16" aria-hidden="true">
        <path
          d="M1.3,16c-0.7,0-1.1-0.3-1.2-0.8c-0.3-0.8,0.5-1.3,0.8-1.5c0.6-0.4,0.9-0.7,1-1c0-0.2-0.1-0.4-0.3-0.7c0,0,0-0.1-0.1-0.1 C0.5,10.6,0,9,0,7.4C0,3.3,3.4,0,7.5,0C11.6,0,15,3.3,15,7.4s-3.4,7.4-7.5,7.4c-0.5,0-1-0.1-1.5-0.2C3.4,15.9,1.5,16,1.5,16 C1.4,16,1.4,16,1.3,16z M3.3,10.9c0.5,0.7,0.7,1.5,0.6,2.2c0,0.1-0.1,0.3-0.1,0.4c0.5-0.2,1-0.4,1.6-0.7c0.2-0.1,0.4-0.2,0.6-0.1 c0,0,0.1,0,0.1,0c0.4,0.1,0.9,0.2,1.4,0.2c3,0,5.5-2.4,5.5-5.4S10.5,2,7.5,2C4.5,2,2,4.4,2,7.4c0,1.2,0.4,2.4,1.2,3.3 C3.2,10.8,3.3,10.8,3.3,10.9z"
        />
      </svg>

      {#if loading}
        <span class="zendesk__label">Loading...</span>
      {/if}
    </button>
  {:else}
    <button
      id="myLauncher"
      class={`zendesk `}
      aria-label="Launch Zendesk chat"
      on:click={() => {
        loadAndOpenZendeskChat();
      }}
      hidden
    >
      <svg class="zendesk__icon" viewBox="0 0 15 16" aria-hidden="true">
        <path
          d="M1.3,16c-0.7,0-1.1-0.3-1.2-0.8c-0.3-0.8,0.5-1.3,0.8-1.5c0.6-0.4,0.9-0.7,1-1c0-0.2-0.1-0.4-0.3-0.7c0,0,0-0.1-0.1-0.1 C0.5,10.6,0,9,0,7.4C0,3.3,3.4,0,7.5,0C11.6,0,15,3.3,15,7.4s-3.4,7.4-7.5,7.4c-0.5,0-1-0.1-1.5-0.2C3.4,15.9,1.5,16,1.5,16 C1.4,16,1.4,16,1.3,16z M3.3,10.9c0.5,0.7,0.7,1.5,0.6,2.2c0,0.1-0.1,0.3-0.1,0.4c0.5-0.2,1-0.4,1.6-0.7c0.2-0.1,0.4-0.2,0.6-0.1 c0,0,0.1,0,0.1,0c0.4,0.1,0.9,0.2,1.4,0.2c3,0,5.5-2.4,5.5-5.4S10.5,2,7.5,2C4.5,2,2,4.4,2,7.4c0,1.2,0.4,2.4,1.2,3.3 C3.2,10.8,3.3,10.8,3.3,10.9z"
        />
      </svg>
    </button>
  {/if}
</div>
